import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Clock, ChefHat, ArrowRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { getRecipeById } from '@/pages/recipes/recipesData'

interface RelatedRecipesSectionProps {
  recipeIds: string[]
}

export function RelatedRecipesSection({ recipeIds }: RelatedRecipesSectionProps) {
  const { t } = useTranslation('common')

  // Get recipe data for the provided IDs from the central dataset
  const recipes = recipeIds
    .map(id => getRecipeById(id))
    .filter(Boolean)

  // If no recipes found, don't render the section
  if (recipes.length === 0) {
    return null
  }

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return t('recipes.featured.easy')
      case 'medium':
        return t('recipes.featured.medium')
      case 'hard':
        return t('recipes.featured.hard')
      default:
        return difficulty
    }
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-700'
      case 'medium':
        return 'bg-yellow-100 text-yellow-700'
      case 'hard':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <section className="py-16 md:py-24 bg-[#FDF8F3]">
      <div className="container-fiorucci">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-[#DF631E]/10 text-[#DF631E] text-sm font-semibold uppercase tracking-wider rounded-full mb-4">
            {t('productDetail.relatedRecipes.badge')}
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#4E2118] mb-4">
            {t('productDetail.relatedRecipes.title')}
          </h2>
          <p className="text-[#4E2118]/70 max-w-2xl mx-auto">
            {t('productDetail.relatedRecipes.description')}
          </p>
        </motion.div>

        {/* Recipes Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {recipes.map((recipe, index) => (
            <motion.article
              key={recipe!.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              {/* Recipe Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <motion.img
                  src={recipe!.image}
                  alt={t(`recipes.recipes.${recipe!.translationKey}.title`)}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118]/60 via-transparent to-transparent" />

                {/* Difficulty Badge */}
                <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(recipe!.difficulty)}`}>
                  {getDifficultyLabel(recipe!.difficulty)}
                </div>

                {/* Time Badge */}
                <div className="absolute bottom-4 right-4 flex items-center gap-1 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-sm font-medium text-[#4E2118]">
                  <Clock className="w-4 h-4" />
                  {recipe!.time}
                </div>
              </div>

              {/* Recipe Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <ChefHat className="w-4 h-4 text-[#DF631E]" />
                  <span className="text-sm text-[#4E2118]/60 capitalize">
                    {recipe!.category}
                  </span>
                </div>

                <h3 className="font-serif text-xl font-semibold text-[#4E2118] mb-2 group-hover:text-[#DF631E] transition-colors">
                  {t(`recipes.recipes.${recipe!.translationKey}.title`)}
                </h3>

                <p className="text-[#4E2118]/70 text-sm line-clamp-2 mb-4">
                  {t(`recipes.recipes.${recipe!.translationKey}.description`)}
                </p>

                <LocalizedLink
                  to={`recipes/${recipe!.id}`}
                  className="inline-flex items-center gap-2 text-[#DF631E] font-semibold hover:gap-3 transition-all"
                >
                  {t('recipes.featured.viewRecipe')}
                  <ArrowRight className="w-4 h-4" />
                </LocalizedLink>
              </div>
            </motion.article>
          ))}
        </div>

        {/* View All Recipes CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-12"
        >
          <LocalizedLink
            to="recipes"
            className="btn-primary"
          >
            {t('productDetail.relatedRecipes.viewAll')}
          </LocalizedLink>
        </motion.div>
      </div>
    </section>
  )
}
